import { PageSubmitLayout } from 'components/Layout/PageBaseLayout';
import { InputLayout } from 'components/Layout/InputLayout';
import { useBffFetch } from 'api/getData';
import { RowContainer } from 'components/Containers';
import { SelectField } from '@flash-tecnologia/hros-web-ui-v2';
import { useEffect, useState, useRef, useCallback } from 'react';
import { dispatchToast, dispatchToastErrorHandler } from 'utils/dispatchToast';
import { useNavigate } from 'react-router-dom';
import { CompanyShareholderSchema } from 'bff/src/application/schemas/company.schema';
import { useSelectedCompany } from '@flash-tecnologia/hros-web-utility';
import { onlyNumbers } from 'utils/masks';
import {
  ShareholderFormItem,
  ShareholderFormItemRef,
} from 'pages/shareholders/components/ShareholderFormItem';
import { Shareholder } from 'utils/shareholderType';

const SESSION_KEY = 'shareholders_form_cache';

export const Shareholders = () => {
  const navigate = useNavigate();
  const { selectedCompany } = useSelectedCompany();

  const [numberOfShareholders, setNumberOfShareholders] = useState<number>(0);
  const [shareholder, setShareholder] = useState<Shareholder[]>([]);
  const shareholderFormRefs = useRef<(ShareholderFormItemRef | null)[]>([]);

  useEffect(() => {
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      setShareholder(parsed.data || []);
      setNumberOfShareholders(parsed.numberOfShareholders || 0);
    }
  }, []);

  useEffect(() => {
    const cached = sessionStorage.getItem(SESSION_KEY);

    if (cached && selectedCompany.id === JSON.parse(cached).companyId) {
      return;
    }
    setNumberOfShareholders(0);
    setShareholder([]);
    const oldCacheKey = `shareholderFormData-${selectedCompany.id}`;
    sessionStorage.removeItem(oldCacheKey);
  }, [selectedCompany, setNumberOfShareholders, setShareholder]);

  useEffect(() => {
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        companyId: selectedCompany.id,
        numberOfShareholders,
        data: shareholder,
      }),
    );
  }, [numberOfShareholders, shareholder]);

  useEffect(() => {
    ajustNumberOfShareholders();
  }, [numberOfShareholders]);

  function ajustNumberOfShareholders() {
    shareholderFormRefs.current = shareholderFormRefs.current.slice(
      0,
      numberOfShareholders,
    );
  }

  const { loading, fetchBff } = useBffFetch<CompanyShareholderSchema, unknown>({
    path: `/v1/companies/${selectedCompany.id}/shareholders`,
    method: 'POST',
    onSuccess: () => {
      sessionStorage.removeItem(SESSION_KEY);
      dispatchToast({
        title: 'Sucesso',
        content: 'Informações enviadas com sucesso!',
        type: 'success',
      });
      navigate('/');
    },
    onError: (err) => dispatchToastErrorHandler(err.statusCode),
  });

  type SingleShareholderType = CompanyShareholderSchema[number];

  const handleSubmit = () => {
    const shareholdersToSend: SingleShareholderType[] = [];

    let isAllFormsValid = true;

    for (let i = 0; i < numberOfShareholders; i++) {
      const formItemRef = shareholderFormRefs.current[i];

      if (formItemRef) {
        const shareholderInfo = formItemRef.getData();

        if (shareholderInfo && shareholderInfo.data) {
          const processedData = { ...shareholderInfo.data };

          if (processedData.document) {
            processedData.document = onlyNumbers(processedData.document);
          }

          processedData.isPF = shareholderInfo.type === 'individual';
          shareholdersToSend.push(processedData as SingleShareholderType);
        }

        if (!formItemRef.triggerValidation()) {
          isAllFormsValid = false;
        }
      } else {
        isAllFormsValid = false;
      }
    }

    if (isAllFormsValid) {
      const totalParticipation = shareholdersToSend.reduce(
        (sum, shareholder) => {
          return sum + (shareholder.participation || 0);
        },
        0,
      );

      if (totalParticipation < 75) {
        dispatchToast({
          title: 'Participação Insuficiente',
          content: `A soma da participação dos sócios deve ser de no mínimo 75%. A soma atual é de ${totalParticipation}%.`,
          type: 'error',
        });
      } else {
        fetchBff(shareholdersToSend as CompanyShareholderSchema);
      }
    } else {
      dispatchToast({
        title: 'Formulário Inválido',
        content: 'Por favor, corrija os campos destacados.',
        type: 'error',
      });
    }
  };

  const handleDataChange = useCallback((i: number, data: any) => {
    setShareholder((prev) => {
      const newCache = [...prev];
      newCache[i] = data;
      return newCache;
    });
  }, []);

  return (
    <PageSubmitLayout
      pageTitle="Informações de proprietários, sócios ou diretores"
      descriptionTitle="Documentos adicionais"
      descriptionSubTitle="Envie os documentos complementares para regularização da empresa na plataforma."
      onSubmit={() => handleSubmit()}
      onCancel={() => navigate('/')}
      loading={loading}
    >
      <InputLayout title="Qual o número mínimo de sócios para atingir 75% da participação?">
        <RowContainer style={{ marginTop: '15px', marginLeft: '-10px' }}>
          <SelectField
            label="Número de sócios"
            value={numberOfShareholders === 0 ? '' : numberOfShareholders}
            style={{ marginTop: '25px', width: '320px' }}
            fullWidth
            options={Array.from({ length: 20 }, (_, i) => ({
              value: i + 1,
              label: (i + 1).toString(),
            }))}
            onSelectChange={(_, option) =>
              setNumberOfShareholders(option.value)
            }
            error={false}
            helperText={null}
          />
        </RowContainer>
      </InputLayout>

      {Array.from({ length: numberOfShareholders }, (_, i) => (
        <ShareholderFormItem
          key={i}
          index={i}
          ref={(el) => (shareholderFormRefs.current[i] = el)}
          initialData={shareholder[i]}
          onDataChange={(data) => handleDataChange(i, data)}
        />
      ))}
    </PageSubmitLayout>
  );
};
